import {
  applyLayerSettingsToLayersAtom,
  LayerData,
  layerDefaultSettings,
  layerSettingsAtom,
  layersAtom,
  tLayerSettings,
  withPreservedTransform,
} from '@/atoms/layers.atom';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { execute } from '@/graphql/execute';
import { useLayerSettings } from '@/hooks/useLayerSettings';
import { analytics } from '@/lib/analytics';
import { autoClassColor } from '@/lib/fragmentColors';
import { useDialog } from '@/providers/DialogProvider';
import { updateLayerSettingsMutation, updateLayersSettingsMutation } from '@/queries/layer.query';
import { useFormik } from 'formik';
import { useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getFragments } from './IFC2';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { NumericField } from './ui/numeric-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';

interface ViewerSettingsDialogProps {
  layer: LayerData & { subLayers: LayerData[] };
  isEditor: boolean;
}

const ClassificationItem = memo(
  ({
    index,
    isEnabled,
    color,
    onToggle,
    onColorChange,
  }: {
    index: number;
    isEnabled: boolean;
    color: string;
    onToggle: (index: number) => void;
    onColorChange: (index: number, color: string) => void;
  }) => {
    const _handleToggle = useCallback(() => {
      onToggle(index);
    }, [index, onToggle]);

    const _handleColorChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onColorChange(index, e.target.value);
      },
      [index, onColorChange],
    );

    const { t } = useTranslation('components');

    return (
      <div className='flex gap-2 items-center border-b last:border-b-0 border-slate-200/70 pl-3 py-2 dark:border-stone-700/40'>
        <Checkbox checked={isEnabled} onCheckedChange={_handleToggle} />
        <input
          type='color'
          id={`point-cloud-material-color-${index}`}
          className='size-8 rounded-full border-none'
          value={color}
          onChange={_handleColorChange}
        />
        <Label htmlFor={`point-cloud-material-color-${index}`}>
          {t('layerSettings.classificationIndex', { index })}
        </Label>
      </div>
    );
  },
);

ClassificationItem.displayName = 'ClassificationItem';

export const DebouncedColorInput = memo(
  ({
    value,
    onChange,
    id,
    className,
  }: {
    value: string;
    onChange: (value: string) => void;
    id: string;
    className: string;
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const _handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => {
          onChange(newValue);
        }, 150); // 150ms debounce
      },
      [onChange],
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return <input type='color' id={id} className={className} value={localValue} onChange={_handleChange} />;
  },
);

DebouncedColorInput.displayName = 'DebouncedColorInput';

/**
 * Scope of the one-shot "Apply to" copy in the dialog footer: just this
 * layer (default, the pre-existing behavior), every same-type layer in
 * the source layer's folder (recursive through subfolders), or every
 * same-type layer in the project. The per-layer transform is never
 * copied to other layers — see withPreservedTransform.
 */
type ApplyScope = 'THIS' | 'FOLDER' | 'PROJECT';

export function LayerSettingsDialog({ layer, isEditor }: ViewerSettingsDialogProps) {
  const { id: layerId, type: layerType } = layer;
  const { t } = useTranslation('components');

  const setLayerSettings = useSetAtom(layerSettingsAtom(layerId));
  const { settings: layerSettings } = useLayerSettings(layerId);
  const dialog = useDialog();

  const allLayers = useAtomValue(layersAtom);
  const applyToLayers = useSetAtom(applyLayerSettingsToLayersAtom);
  const [applyScope, setApplyScope] = useState<ApplyScope>('THIS');

  // Other layers of the same type anywhere in the project.
  const projectTargets = useMemo(
    () => allLayers.filter((l) => l.type === layerType && l.id !== layerId),
    [allLayers, layerType, layerId],
  );

  // Other layers of the same type under the source layer's parent folder,
  // recursively through subfolders — consistent with how the visibility
  // cascade treats groups. Empty for root-level layers (project scope
  // covers that case).
  const folderTargets = useMemo(() => {
    if (!layer.parentLayerId) return [];
    const folderIds = new Set<string>([layer.parentLayerId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const l of allLayers) {
        if (l.type === 'GROUP' && l.parentLayerId && folderIds.has(l.parentLayerId) && !folderIds.has(l.id)) {
          folderIds.add(l.id);
          grew = true;
        }
      }
    }
    return allLayers.filter(
      (l) => l.type === layerType && l.id !== layerId && l.parentLayerId && folderIds.has(l.parentLayerId),
    );
  }, [allLayers, layer.parentLayerId, layerType, layerId]);

  const targetLayers = applyScope === 'PROJECT' ? projectTargets : applyScope === 'FOLDER' ? folderTargets : [];
  const typeLabel = t(`layerSettings.layerTypeNames.${layerType}`);

  const layerDefaults = layerDefaultSettings(layer);

  // Capture initial values once on mount — the dialog is always a fresh instance
  // per layer (new dialog ID), so we don't need enableReinitialize.
  const initialValues = useRef<tLayerSettings>({
    ...layerDefaults,
    ...(layerSettings || {}),
  }).current;

  // Local (per-user) save: the source layer's atom always gets the raw
  // values; targets get them with their own transform preserved.
  const _applyLocally = (values: tLayerSettings) => {
    setLayerSettings(values);
    if (targetLayers.length > 0) {
      applyToLayers({ layerIds: targetLayers.map((l) => l.id), settings: values });
    }
  };

  const formik = useFormik<tLayerSettings>({
    initialValues,
    onSubmit: async (values) => {
      _applyLocally(values);
      if (targetLayers.length > 0) {
        analytics.capture('Layer Settings Applied To Multiple Layers', {
          scope: applyScope,
          layerCount: targetLayers.length + 1,
        });
        toast.success(t('layerSettings.appliedToLayers', { count: targetLayers.length + 1 }));
      }
      dialog.dismissActiveDialog();
    },
  });

  const _handleClose = useCallback(() => {
    dialog.dismissActiveDialog();
  }, [dialog]);

  const _handleSaveForEveryone = async () => {
    analytics.capture('Layer Settings Saved For Everyone', {
      scope: applyScope,
      layerCount: targetLayers.length + 1,
    });
    // Single-layer scope keeps the original mutation; multi-layer scope
    // sends one fully-populated settings object per target with that
    // target's own remote transform preserved (the resolver replaces the
    // whole settings JSONB, so partial inputs aren't an option).
    const promise: Promise<unknown> =
      targetLayers.length === 0
        ? execute(updateLayerSettingsMutation, { id: layerId, input: formik.values })
        : execute(updateLayersSettingsMutation, {
            input: [
              { id: layerId, settings: formik.values },
              ...targetLayers.map((target) => ({
                id: target.id,
                settings: withPreservedTransform(formik.values, target.settings),
              })),
            ],
          });
    toast.promise(promise, {
      loading: t('layerSettings.savingForEveryone'),
      success: t('layerSettings.savedForEveryone'),
      error: t('layerSettings.failedSaveForEveryone'),
    });
    // Mirror the plain-save path (write local atoms + close) without going
    // through formik.submitForm, which would fire a second toast.
    _applyLocally(formik.values);
    dialog.dismissActiveDialog();
  };

  const numberOfClasses =
    (layer.metadata?.potreeMetadata?.attributes.find((attribute: any) => attribute.name === 'classification')?.max[0] ||
      255) + 1;

  const _handleClassificationToggle = useCallback(
    (index: number) => {
      const newClasses = [...(formik.values.pointCloudClasses || [])];
      if (!newClasses[index]) {
        newClasses[index] = { isEnabled: false, color: '#000000' };
      }
      newClasses[index] = {
        ...newClasses[index],
        isEnabled: !newClasses[index].isEnabled,
      };
      formik.setFieldValue('pointCloudClasses', newClasses);
    },
    [formik],
  );

  const _handleClassificationColorChange = useCallback(
    (index: number, color: string) => {
      const newClasses = [...(formik.values.pointCloudClasses || [])];
      if (!newClasses[index]) {
        newClasses[index] = { isEnabled: false, color: '#000000' };
      }
      newClasses[index] = {
        ...newClasses[index],
        color,
      };
      formik.setFieldValue('pointCloudClasses', newClasses);
    },
    [formik],
  );

  // Memoize model color change handler
  const _handleModelColorChange = useCallback(
    (color: string) => {
      formik.setFieldValue('modelDefaultColor', color);
    },
    [formik],
  );

  // Memoize the classification list to prevent unnecessary re-renders
  const classificationItems = useMemo(() => {
    return Array.from({ length: numberOfClasses }).map((_, index) => {
      const classification = formik.values.pointCloudClasses?.[index];
      return (
        <ClassificationItem
          key={index}
          index={index}
          isEnabled={classification?.isEnabled || false}
          color={classification?.color || '#000000'}
          onToggle={_handleClassificationToggle}
          onColorChange={_handleClassificationColorChange}
        />
      );
    });
  }, [formik.values.pointCloudClasses, _handleClassificationToggle, _handleClassificationColorChange, numberOfClasses]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('layerSettings.title')}</DialogTitle>
        <DialogDescription>{t('layerSettings.description')}</DialogDescription>
      </DialogHeader>
      <form onSubmit={formik.handleSubmit}>
        <div className='grid gap-6 py-4 max-h-[60vh] overflow-auto'>
          {layerType === 'POINTCLOUD' && (
            <>
              <div className='grid grid-cols-2 gap-6'>
                <div className='flex flex-col gap-2'>
                  <Label htmlFor='point-cloud-material-color-source'>{t('layerSettings.materialColorSource')}</Label>
                  <Select
                    value={formik.values.pointCloudMaterialColorSource.toString()}
                    onValueChange={(value) => formik.setFieldValue('pointCloudMaterialColorSource', parseInt(value))}>
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder={t('layerSettings.selectMaterialColorSource')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'rgb',
                        )}
                        value='0'>
                        {t('layerSettings.rgb')}
                      </SelectItem>
                      {/* <SelectItem value='2'>Depth</SelectItem> */}
                      <SelectItem value='3'>{t('layerSettings.elevation')}</SelectItem>
                      {/* <SelectItem value='4'>Intensity</SelectItem> */}
                      <SelectItem value='5'>{t('layerSettings.intensityGradient')}</SelectItem>
                      {/* <SelectItem value='6'>LOD</SelectItem> */}
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'classification',
                        )}
                        value='8'>
                        {t('layerSettings.classification')}
                      </SelectItem>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'return number',
                        )}
                        value='9'>
                        {t('layerSettings.returnNumber')}
                      </SelectItem>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'point source id',
                        )}
                        value='10'>
                        {t('layerSettings.sourceId')}
                      </SelectItem>
                      {/* <SelectItem value='13'>RGB Height</SelectItem> */}
                      <SelectItem value='1'>{t('layerSettings.fixedColor')}</SelectItem>
                      <SelectItem
                        disabled={
                          !(layer.metadata as any)?.potreeMetadata?.attributes?.some?.(
                            (a: { name?: string }) => a.name === 'Deviation',
                          )
                        }
                        value='51'>
                        {t('layerSettings.deviation')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {['3', '5', '51'].includes(formik.values.pointCloudMaterialColorSource.toString()) ? (
                  <div className='flex flex-col gap-2'>
                    <Label htmlFor='point-cloud-material-color'>{t('layerSettings.materialColor')}</Label>
                    <Select
                      value={formik.values.pointCloudMaterialColor}
                      onValueChange={(value) => formik.setFieldValue('pointCloudMaterialColor', value)}>
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder={t('layerSettings.selectMaterialColor')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='GRAYSCALE'>{t('layerSettings.grayscale')}</SelectItem>
                        <SelectItem value='RAINBOW'>{t('layerSettings.rainbow')}</SelectItem>
                        <SelectItem value='SPECTRAL'>{t('layerSettings.spectral')}</SelectItem>
                        <SelectItem value='PLASMA'>{t('layerSettings.plasma')}</SelectItem>
                        <SelectItem value='YELLOW_GREEN'>{t('layerSettings.yellowGreen')}</SelectItem>
                        <SelectItem value='VIRIDIS'>{t('layerSettings.viridis')}</SelectItem>
                        <SelectItem value='INFERNO'>{t('layerSettings.inferno')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>
              {[51].includes(parseInt(String(formik.values.pointCloudMaterialColorSource))) && (
                <div className='flex items-start gap-1 flex-col'>
                  <Label>{t('layerSettings.displayRange')}</Label>
                  <div className='flex items-center gap-2 w-full'>
                    <Input
                      type='number'
                      step='0.001'
                      placeholder={String((layer.metadata as any)?.deviationRange?.[0] ?? -0.05)}
                      value={formik.values.scalarRangeMin ?? ''}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'scalarRangeMin',
                          e.target.value === '' ? null : parseFloat(e.target.value),
                        )
                      }
                      className='flex-1'
                    />
                    <span className='text-xs text-slate-500'>—</span>
                    <Input
                      type='number'
                      step='0.001'
                      placeholder={String((layer.metadata as any)?.deviationRange?.[1] ?? 0.05)}
                      value={formik.values.scalarRangeMax ?? ''}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'scalarRangeMax',
                          e.target.value === '' ? null : parseFloat(e.target.value),
                        )
                      }
                      className='flex-1'
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        formik.setFieldValue('scalarRangeMin', null);
                        formik.setFieldValue('scalarRangeMax', null);
                      }}>
                      {t('layerSettings.resetRange')}
                    </Button>
                  </div>
                  <span className='text-xs text-slate-500'>{t('layerSettings.displayRangeHelp')}</span>
                </div>
              )}
              {formik.values.pointCloudMaterialColorSource === 1 && (
                <div className='flex items-center gap-2'>
                  <DebouncedColorInput
                    id='point-cloud-fixed-color'
                    className='size-8 rounded-full border-none'
                    value={formik.values.pointCloudFixedColor}
                    onChange={(value) => formik.setFieldValue('pointCloudFixedColor', value)}
                  />
                  <Label htmlFor='point-cloud-fixed-color'>{t('layerSettings.fixedColorLabel')}</Label>
                </div>
              )}
              {layer.metadata.potreeMetadata.attributes.some((attribute: any) => attribute.name === 'classification') &&
                formik.values.pointCloudMaterialColorSource === 8 && (
                  <div className='flex flex-col gap-2'>
                    <Label htmlFor='point-cloud-material-color'>{t('layerSettings.classifications')}</Label>
                    <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40'>
                      {classificationItems}
                    </div>
                  </div>
                )}
            </>
          )}
          {layerType === 'MODEL' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='model-default-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.modelDefaultColor}
                  onChange={_handleModelColorChange}
                />
                <Label htmlFor='model-default-color'>{t('layerSettings.defaultColor')}</Label>
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='model-is-wireframe'
                  checked={formik.values.modelIsWireframe}
                  onCheckedChange={(checked) => formik.setFieldValue('modelIsWireframe', checked)}
                />
                <Label htmlFor='model-is-wireframe'>{t('layerSettings.showWireframe')}</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='model-opacity'>
                  {t('layerSettings.modelOpacity')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {(formik.values.modelOpacity * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='model-opacity'
                  value={[formik.values.modelOpacity * 100]}
                  min={0}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('modelOpacity', value[0] / 100)}
                />
              </div>
              {(layer.metadata as any)?.type === 'FRAGMENT' && (
                <FragmentColorByControls
                  layerId={layerId}
                  mode={formik.values.modelColorBy}
                  classColors={formik.values.modelClassColors}
                  onModeChange={(v) => formik.setFieldValue('modelColorBy', v)}
                  onClassColorsChange={(v) => formik.setFieldValue('modelClassColors', v)}
                />
              )}
              {(layer.metadata as any)?.type === 'LANDXML' && (
                <LandXMLControls
                  categoryColors={formik.values.landxmlCategoryColors}
                  surfaceMode={formik.values.landxmlSurfaceColorMode}
                  surfaceColormap={formik.values.landxmlSurfaceColormap}
                  onCategoryColorChange={(category, color) =>
                    formik.setFieldValue('landxmlCategoryColors', {
                      ...formik.values.landxmlCategoryColors,
                      [category]: color,
                    })
                  }
                  onSurfaceModeChange={(v) => formik.setFieldValue('landxmlSurfaceColorMode', v)}
                  onSurfaceColormapChange={(v) => formik.setFieldValue('landxmlSurfaceColormap', v)}
                />
              )}
            </>
          )}
          {layerType === 'IMAGE' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='image-point-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.imagePointColor}
                  onChange={(value) => formik.setFieldValue('imagePointColor', value)}
                />
                <Label htmlFor='image-point-color'>{t('layerSettings.imagePoiColor')}</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='image-brightness'>
                  {t('layerSettings.imageBrightness')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.imageBrightness > 0 ? '+' : ''}
                    {formik.values.imageBrightness.toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='image-brightness'
                  value={[formik.values.imageBrightness]}
                  min={-50}
                  max={50}
                  onValueChange={(value) => formik.setFieldValue('imageBrightness', value[0])}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='image-contrast'>
                  {t('layerSettings.imageContrast')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.imageContrast > 0 ? '+' : ''}
                    {formik.values.imageContrast.toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='image-contrast'
                  value={[formik.values.imageContrast]}
                  min={-50}
                  max={50}
                  onValueChange={(value) => formik.setFieldValue('imageContrast', value[0])}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='image-saturation'>
                  {t('layerSettings.imageSaturation')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.imageSaturation > 0 ? '+' : ''}
                    {formik.values.imageSaturation.toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='image-saturation'
                  value={[formik.values.imageSaturation]}
                  min={-100}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('imageSaturation', value[0])}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='image-opacity'>
                  {t('layerSettings.imageOpacity')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {(formik.values.imageOpacity * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='image-opacity'
                  value={[formik.values.imageOpacity * 100]}
                  min={0}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('imageOpacity', value[0] / 100)}
                />
              </div>
            </>
          )}
          {(layerType === 'TILE' || layerType === 'WMS') && (
            <>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='raster-opacity'>
                  {t('layerSettings.rasterOpacity')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {(formik.values.rasterOpacity * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='raster-opacity'
                  value={[formik.values.rasterOpacity * 100]}
                  min={0}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('rasterOpacity', value[0] / 100)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='raster-brightness'>
                  {t('layerSettings.rasterBrightness')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.rasterBrightness > 0 ? '+' : ''}
                    {(formik.values.rasterBrightness * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='raster-brightness'
                  value={[formik.values.rasterBrightness * 100]}
                  min={-90}
                  max={90}
                  onValueChange={(value) => formik.setFieldValue('rasterBrightness', value[0] / 100)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='raster-contrast'>
                  {t('layerSettings.rasterContrast')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.rasterContrast > 0 ? '+' : ''}
                    {(formik.values.rasterContrast * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='raster-contrast'
                  value={[formik.values.rasterContrast * 100]}
                  min={-70}
                  max={70}
                  onValueChange={(value) => formik.setFieldValue('rasterContrast', value[0] / 100)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='raster-saturation'>
                  {t('layerSettings.rasterSaturation')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.rasterSaturation > 0 ? '+' : ''}
                    {(formik.values.rasterSaturation * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='raster-saturation'
                  value={[formik.values.rasterSaturation * 100]}
                  min={-70}
                  max={70}
                  onValueChange={(value) => formik.setFieldValue('rasterSaturation', value[0] / 100)}
                />
              </div>
            </>
          )}
          {layerType === 'VECTOR' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='vector-default-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.vectorDefaultColor}
                  onChange={(value) => formik.setFieldValue('vectorDefaultColor', value)}
                />
                <Label htmlFor='vector-default-color'>{t('layerSettings.vectorDefaultColor')}</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='vector-default-opacity whitespace-nowrap'>
                  {t('layerSettings.vectorDefaultOpacity')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {(formik.values.vectorDefaultOpacity * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='vector-default-opacity'
                  value={[formik.values.vectorDefaultOpacity * 100]}
                  min={0}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('vectorDefaultOpacity', value[0] / 100)}
                />
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='vector-is-wireframe'
                  checked={formik.values.vectorIsWireframe}
                  onCheckedChange={(checked) => formik.setFieldValue('vectorIsWireframe', checked)}
                />
                <Label htmlFor='vector-is-wireframe'>{t('layerSettings.showWireframe')}</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='vector-default-point-size whitespace-nowrap'>
                  {t('layerSettings.vectorDefaultPointSize')}{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {Number(formik.values.vectorDefaultPointSize * 100).toFixed(0)} cm
                  </span>
                </Label>
                <Slider
                  id='vector-default-opacity'
                  value={[formik.values.vectorDefaultPointSize]}
                  min={0}
                  step={0.01}
                  max={1}
                  onValueChange={(value) => formik.setFieldValue('vectorDefaultPointSize', Number(value[0].toFixed(2)))}
                />
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='vector-line-render-as-pipe'
                  checked={formik.values.vectorLineRenderAsPipe}
                  onCheckedChange={(checked) => formik.setFieldValue('vectorLineRenderAsPipe', checked)}
                />
                <Label htmlFor='vector-line-render-as-pipe'>{t('layerSettings.vectorLineRenderAsPipe')}</Label>
              </div>
              {formik.values.vectorLineRenderAsPipe && (
                <div className='flex flex-col gap-2'>
                  <Label htmlFor='vector-line-width whitespace-nowrap'>
                    {t('layerSettings.vectorLineWidth')}{' '}
                    <span className='text-xs font-medium text-muted-foreground font-mono'>
                      {Number(formik.values.vectorLineWidth * 100).toFixed(0)} cm
                    </span>
                  </Label>
                  <Slider
                    id='vector-line-width'
                    value={[formik.values.vectorLineWidth * 100]}
                    min={1}
                    step={1}
                    max={5}
                    onValueChange={(value) => formik.setFieldValue('vectorLineWidth', value[0] / 100)}
                  />
                </div>
              )}
              {/* Surface coloring: ELEVATION / SLOPE / SOLID. The 3D
                  renderer applies vertex colors to polygon features
                  using the chosen colormap. Mirrors the LandXML
                  surface controls so the two analysis layer types feel
                  consistent. The colormap select hides while in SOLID
                  mode — same pattern as LandXMLControls. */}
              <div className='flex flex-col gap-2'>
                <Label htmlFor='vector-surface-mode'>{t('layerSettings.vectorSurfaceMode')}</Label>
                <Select
                  value={formik.values.vectorSurfaceColorMode}
                  onValueChange={(v) => formik.setFieldValue('vectorSurfaceColorMode', v)}>
                  <SelectTrigger className='w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='SOLID'>{t('layerSettings.landxmlSurfaceMode_solid')}</SelectItem>
                    <SelectItem value='ELEVATION'>{t('layerSettings.landxmlSurfaceMode_elevation')}</SelectItem>
                    <SelectItem value='SLOPE'>{t('layerSettings.landxmlSurfaceMode_slope')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formik.values.vectorSurfaceColorMode !== 'SOLID' && (
                <div className='flex flex-col gap-2'>
                  <Label htmlFor='vector-surface-colormap'>{t('layerSettings.materialColor')}</Label>
                  <Select
                    value={formik.values.vectorSurfaceColormap}
                    onValueChange={(v) => formik.setFieldValue('vectorSurfaceColormap', v)}>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='GRAYSCALE'>{t('layerSettings.grayscale')}</SelectItem>
                      <SelectItem value='RAINBOW'>{t('layerSettings.rainbow')}</SelectItem>
                      <SelectItem value='SPECTRAL'>{t('layerSettings.spectral')}</SelectItem>
                      <SelectItem value='PLASMA'>{t('layerSettings.plasma')}</SelectItem>
                      <SelectItem value='YELLOW_GREEN'>{t('layerSettings.yellowGreen')}</SelectItem>
                      <SelectItem value='VIRIDIS'>{t('layerSettings.viridis')}</SelectItem>
                      <SelectItem value='INFERNO'>{t('layerSettings.inferno')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
          {/* Per-layer transform: offset (X/Y/Z in metres) and rotation
              (X/Y/Z in degrees, applied as Euler XYZ at render time).
              Available for every layer type that renders in the 3D
              viewer (POINTCLOUD, MODEL, VECTOR). TILE / WMS are 2D
              Mapbox raster sources where per-layer translation isn't
              meaningful, so we hide the section for those types. */}
          {(layerType === 'POINTCLOUD' || layerType === 'MODEL' || layerType === 'VECTOR') && (
            <TransformControls
              offsetX={formik.values.offsetX}
              offsetY={formik.values.offsetY}
              offsetZ={formik.values.offsetZ}
              rotationX={formik.values.rotationX}
              rotationY={formik.values.rotationY}
              rotationZ={formik.values.rotationZ}
              onOffsetXChange={(v) => formik.setFieldValue('offsetX', v)}
              onOffsetYChange={(v) => formik.setFieldValue('offsetY', v)}
              onOffsetZChange={(v) => formik.setFieldValue('offsetZ', v)}
              onRotationXChange={(v) => formik.setFieldValue('rotationX', v)}
              onRotationYChange={(v) => formik.setFieldValue('rotationY', v)}
              onRotationZChange={(v) => formik.setFieldValue('rotationZ', v)}
              onReset={() => {
                formik.setFieldValue('offsetX', 0);
                formik.setFieldValue('offsetY', 0);
                formik.setFieldValue('offsetZ', 0);
                formik.setFieldValue('rotationX', 0);
                formik.setFieldValue('rotationY', 0);
                formik.setFieldValue('rotationZ', 0);
              }}
            />
          )}
        </div>
        {/* One-shot copy to other layers of the same type. Kept outside the
            scroll container so it's always visible next to the buttons it
            modifies. Hidden when this is the only layer of its type. */}
        {projectTargets.length > 0 && (
          <div className='flex flex-col gap-2 border-t border-slate-200/70 pt-3 pb-1 dark:border-stone-700/40'>
            <Label htmlFor='apply-scope'>{t('layerSettings.applyTo')}</Label>
            <Select value={applyScope} onValueChange={(value) => setApplyScope(value as ApplyScope)}>
              <SelectTrigger id='apply-scope' className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='THIS'>{t('layerSettings.applyScopeThis')}</SelectItem>
                {folderTargets.length > 0 && (
                  <SelectItem value='FOLDER'>
                    {t('layerSettings.applyScopeFolder', { type: typeLabel, count: folderTargets.length + 1 })}
                  </SelectItem>
                )}
                <SelectItem value='PROJECT'>
                  {t('layerSettings.applyScopeProject', { type: typeLabel, count: projectTargets.length + 1 })}
                </SelectItem>
              </SelectContent>
            </Select>
            {targetLayers.length > 0 && (
              <span className='text-[11px] leading-snug text-slate-500 dark:text-stone-400'>
                {t('layerSettings.applyTransformNote', { count: targetLayers.length + 1 })}
              </span>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type='button' variant='outline' onClick={_handleClose}>
            {t('layerSettings.cancel')}
          </Button>
          {isEditor && (
            <Button type='button' variant='outline' onClick={_handleSaveForEveryone}>
              {t('layerSettings.saveForEveryone')}
            </Button>
          )}
          <Button type='submit'>{t('layerSettings.saveSettings')}</Button>
        </DialogFooter>
      </form>
    </>
  );
}

LayerSettingsDialog.displayName = 'LayerSettingsDialog';

/**
 * IFC-fragment-only controls: choose between default coloring and
 * coloring by IFC class. State lives in the GraphQL-backed LayerSettings
 * (`modelColorBy` + `modelClassColors`), driven through the parent's
 * formik instance so it follows the same Save / Save-for-everyone path
 * as every other layer setting.
 */
type ClassColor = { class: string; color: string };

const FragmentColorByControls = memo(
  ({
    layerId,
    mode,
    classColors,
    onModeChange,
    onClassColorsChange,
  }: {
    layerId: string;
    mode: string;
    classColors: ClassColor[];
    onModeChange: (mode: string) => void;
    onClassColorsChange: (next: ClassColor[]) => void;
  }) => {
    const { t } = useTranslation('components');
    const [classes, setClasses] = useState<string[]>([]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const fragments = await getFragments();
          const model = fragments.models.list.get(`frag-${layerId}`);
          if (!model) return;
          const cats = await model.getCategories();
          if (!cancelled) setClasses([...cats].sort());
        } catch {
          // model may not be loaded yet
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [layerId]);

    const colorByClass = useMemo(() => {
      const m = new Map<string, string>();
      for (const c of classColors) m.set(c.class, c.color);
      return m;
    }, [classColors]);

    const _handleColorChange = useCallback(
      (cls: string, color: string) => {
        const idx = classColors.findIndex((c) => c.class === cls);
        if (idx === -1) {
          onClassColorsChange([...classColors, { class: cls, color }]);
        } else {
          const next = classColors.slice();
          next[idx] = { class: cls, color };
          onClassColorsChange(next);
        }
      },
      [classColors, onClassColorsChange],
    );

    return (
      <>
        <div className='flex flex-col gap-2'>
          <Label htmlFor='fragment-color-by'>{t('layerSettings.fragmentColorBy')}</Label>
          <Select value={mode} onValueChange={onModeChange}>
            <SelectTrigger className='w-full'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='DEFAULT'>{t('layerSettings.fragmentColorByDefault')}</SelectItem>
              <SelectItem value='CLASS'>{t('layerSettings.fragmentColorByClass')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === 'CLASS' && classes.length > 0 && (
          <div className='flex flex-col gap-2'>
            <Label>{t('layerSettings.fragmentClassColors')}</Label>
            <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40 max-h-60 overflow-y-auto'>
              {classes.map((cls) => {
                const color = colorByClass.get(cls) ?? autoClassColor(cls);
                return (
                  <div
                    key={cls}
                    className='flex gap-2 items-center border-b last:border-b-0 border-slate-200/70 px-3 py-1.5 dark:border-stone-700/40'>
                    <DebouncedColorInput
                      id={`fragment-class-color-${cls}`}
                      className='size-6 rounded-full border-none'
                      value={color}
                      onChange={(value) => _handleColorChange(cls, value)}
                    />
                    <Label htmlFor={`fragment-class-color-${cls}`} className='text-xs font-mono'>
                      {cls}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  },
);
FragmentColorByControls.displayName = 'FragmentColorByControls';

/**
 * LandXML-only controls: per-category colors + surface coloring mode +
 * colormap. All driven through the parent's formik so they go through
 * the regular Save / Save-for-everyone path.
 */
type LandXMLCategoryColors = {
  surface: string;
  alignment: string;
  parcel: string;
  breakline: string;
  pipe: string;
  structure: string;
};

const LANDXML_CATEGORY_KEYS: Array<keyof LandXMLCategoryColors> = [
  'surface',
  'alignment',
  'parcel',
  'breakline',
  'pipe',
  'structure',
];

const LandXMLControls = memo(
  ({
    categoryColors,
    surfaceMode,
    surfaceColormap,
    onCategoryColorChange,
    onSurfaceModeChange,
    onSurfaceColormapChange,
  }: {
    categoryColors: LandXMLCategoryColors;
    surfaceMode: string;
    surfaceColormap: string;
    onCategoryColorChange: (category: keyof LandXMLCategoryColors, color: string) => void;
    onSurfaceModeChange: (mode: string) => void;
    onSurfaceColormapChange: (colormap: string) => void;
  }) => {
    const { t } = useTranslation('components');
    return (
      <>
        <div className='flex flex-col gap-2'>
          <Label>{t('layerSettings.landxmlCategoryColors')}</Label>
          <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40'>
            {LANDXML_CATEGORY_KEYS.map((key) => (
              <div
                key={key}
                className='flex gap-2 items-center border-b last:border-b-0 border-slate-200/70 px-3 py-1.5 dark:border-stone-700/40'>
                <DebouncedColorInput
                  id={`landxml-category-color-${key}`}
                  className='size-6 rounded-full border-none'
                  value={categoryColors[key]}
                  onChange={(value) => onCategoryColorChange(key, value)}
                />
                <Label htmlFor={`landxml-category-color-${key}`} className='text-xs capitalize'>
                  {t(`layerSettings.landxmlCategory.${key}`)}
                </Label>
              </div>
            ))}
          </div>
        </div>
        <div className='flex flex-col gap-2'>
          <Label htmlFor='landxml-surface-mode'>{t('layerSettings.landxmlSurfaceMode')}</Label>
          <Select value={surfaceMode} onValueChange={onSurfaceModeChange}>
            <SelectTrigger className='w-full'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='SOLID'>{t('layerSettings.landxmlSurfaceMode_solid')}</SelectItem>
              <SelectItem value='ELEVATION'>{t('layerSettings.landxmlSurfaceMode_elevation')}</SelectItem>
              <SelectItem value='SLOPE'>{t('layerSettings.landxmlSurfaceMode_slope')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {surfaceMode !== 'SOLID' && (
          <div className='flex flex-col gap-2'>
            <Label htmlFor='landxml-surface-colormap'>{t('layerSettings.materialColor')}</Label>
            <Select value={surfaceColormap} onValueChange={onSurfaceColormapChange}>
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='GRAYSCALE'>{t('layerSettings.grayscale')}</SelectItem>
                <SelectItem value='RAINBOW'>{t('layerSettings.rainbow')}</SelectItem>
                <SelectItem value='SPECTRAL'>{t('layerSettings.spectral')}</SelectItem>
                <SelectItem value='PLASMA'>{t('layerSettings.plasma')}</SelectItem>
                <SelectItem value='YELLOW_GREEN'>{t('layerSettings.yellowGreen')}</SelectItem>
                <SelectItem value='VIRIDIS'>{t('layerSettings.viridis')}</SelectItem>
                <SelectItem value='INFERNO'>{t('layerSettings.inferno')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    );
  },
);
LandXMLControls.displayName = 'LandXMLControls';

/**
 * Per-layer transform controls — offset (metres) and rotation (degrees).
 * Visible for every 3D-rendered layer type (POINTCLOUD, MODEL, VECTOR);
 * gated off for TILE / WMS in the parent (those are 2D Mapbox sources).
 *
 * NumericField is required here (per AGENTS.md numeric-inputs rule)
 * because `<Input type="number">` + `parseFloat(e.target.value)` swallows
 * intermediate states like "-", "-0", "-0." and makes typing negative
 * values nearly impossible.
 */
const TransformControls = memo(
  ({
    offsetX,
    offsetY,
    offsetZ,
    rotationX,
    rotationY,
    rotationZ,
    onOffsetXChange,
    onOffsetYChange,
    onOffsetZChange,
    onRotationXChange,
    onRotationYChange,
    onRotationZChange,
    onReset,
  }: {
    offsetX: number;
    offsetY: number;
    offsetZ: number;
    rotationX: number;
    rotationY: number;
    rotationZ: number;
    onOffsetXChange: (v: number) => void;
    onOffsetYChange: (v: number) => void;
    onOffsetZChange: (v: number) => void;
    onRotationXChange: (v: number) => void;
    onRotationYChange: (v: number) => void;
    onRotationZChange: (v: number) => void;
    onReset: () => void;
  }) => {
    const { t } = useTranslation('components');
    const isIdentity =
      offsetX === 0 && offsetY === 0 && offsetZ === 0 && rotationX === 0 && rotationY === 0 && rotationZ === 0;
    return (
      <div className='flex flex-col gap-3 border-t pt-4 border-slate-200/70 dark:border-stone-700/40'>
        <div className='flex items-center justify-between'>
          <Label>{t('layerSettings.transformTitle')}</Label>
          {!isIdentity && (
            <Button type='button' variant='ghost' size='sm' onClick={onReset}>
              {t('layerSettings.transformReset')}
            </Button>
          )}
        </div>
        <span className='text-xs text-slate-500'>{t('layerSettings.transformHelp')}</span>
        <div className='flex flex-col gap-2'>
          <Label className='text-xs text-muted-foreground uppercase tracking-wide'>
            {t('layerSettings.offsetLabel')}
          </Label>
          <div className='grid grid-cols-3 gap-2'>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.offsetX')}</Label>
              <NumericField value={offsetX} onChange={onOffsetXChange} step='0.01' />
            </div>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.offsetY')}</Label>
              <NumericField value={offsetY} onChange={onOffsetYChange} step='0.01' />
            </div>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.offsetZ')}</Label>
              <NumericField value={offsetZ} onChange={onOffsetZChange} step='0.01' />
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-2'>
          <Label className='text-xs text-muted-foreground uppercase tracking-wide'>
            {t('layerSettings.rotationLabel')}
          </Label>
          <div className='grid grid-cols-3 gap-2'>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.rotationX')}</Label>
              <NumericField value={rotationX} onChange={onRotationXChange} step='1' />
            </div>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.rotationY')}</Label>
              <NumericField value={rotationY} onChange={onRotationYChange} step='1' />
            </div>
            <div className='flex flex-col gap-1'>
              <Label className='text-xs'>{t('layerSettings.rotationZ')}</Label>
              <NumericField value={rotationZ} onChange={onRotationZChange} step='1' />
            </div>
          </div>
        </div>
      </div>
    );
  },
);
TransformControls.displayName = 'TransformControls';
